import React, { useRef } from 'react';
import { LuTriangleAlert, LuZap } from 'react-icons/lu';
import { generateNowPeriod } from '../../utils/reportPeriod.js';
import { describeGenerateNow, formatParisInstant, formatParisWhen, formatSlot } from '../../utils/weekFormat.js';
import { Modal } from '../ui/Modal';
import Alert from '../ui/Alert';
import Button from '../ui/Button';

/**
 * Warns that a slot change pushes the next report later. `pending` is 'save' | 'generate' | null;
 * the dialog stays open on error.
 */
const ScheduleShiftDialog = ({ isOpen, preview, latestWeekly, savedSlot, newSlot, pending, error, onCancel, onSave, onSaveAndGenerate }) => {
  const cancelRef = useRef(null);
  if (!preview?.current || !preview?.proposed) return null;
  const now = new Date();
  // Saving stamps the slot change now; without the latest weekly, the preview's period is the best guess.
  const generated = latestWeekly === undefined
    ? { ...preview.proposed, ongoing: false }
    : generateNowPeriod({ latest: latestWeekly, ...newSlot, scheduleUpdatedAt: now, now });
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      dismissible={!pending}
      initialFocusRef={cancelRef}
      icon={LuTriangleAlert}
      iconTone="amber"
      size="sm"
      title="Le prochain rapport est repoussé"
      footer={(
        <>
          <Button ref={cancelRef} onClick={onCancel} disabled={!!pending}>Annuler</Button>
          <Button variant="soft" onClick={onSave} loading={pending === 'save'} disabled={!!pending}>Enregistrer quand même</Button>
          <Button variant="primary" icon={LuZap} onClick={onSaveAndGenerate} loading={pending === 'generate'} disabled={!!pending}>
            Enregistrer et générer maintenant
          </Button>
        </>
      )}
    >
      <div className="space-y-3 text-sm">
        {error && <Alert tone="danger">{error}</Alert>}
        <p className="text-gray-600 dark:text-gray-300">
          Le rapport prévu {formatParisWhen(preview.current.end)} ne sera pas généré. Prochain rapport : {formatParisInstant(preview.proposed.end, { separator: ' à ' })}.
        </p>
        <div className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 px-3.5 py-3 dark:bg-night-850">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Créneau actuel</p>
            <p className="mt-0.5 text-gray-900 dark:text-white">{formatSlot(savedSlot.day, savedSlot.hour)}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Nouveau créneau</p>
            <p className="mt-0.5 font-medium text-gray-900 dark:text-white">{formatSlot(newSlot.day, newSlot.hour)}</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          « Enregistrer quand même » : aucun rapport avant le {formatParisInstant(preview.proposed.end, { separator: ' à ' })}.
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          « Enregistrer et générer maintenant » {describeGenerateNow(generated, now)}.
        </p>
      </div>
    </Modal>
  );
};

export default ScheduleShiftDialog;
